/**
 * attended-transfer — ITEM 98 / §9.
 *
 * The user-facing half of attended transfer.
 *
 * `session-registry.ts` landed the SAFETY half: two concurrent legs no longer
 * orphan each other, both tear down on logout, and a lost primary promotes the
 * consultation. The FEATURE was still missing — `useSoftphone.transfer()` does
 * `session.hold()` followed by `session.refer(target)` under a comment reading
 * "Attended transfer workflow" (useSoftphone.ts:904). That is a BLIND transfer
 * with a hold in front of it.
 *
 * The distinction is not cosmetic. In a blind transfer the agent has already
 * handed the caller over by the time they discover the target is at lunch;
 * there is no way back, and the caller is gone. Attended transfer means:
 *
 *   consult first  ->  then EITHER complete OR cancel and return to the caller
 *
 * WHY A SEPARATE CONTROLLER
 * -------------------------
 * The decisions (which leg to end, which to resume, whether a transfer may
 * complete) belong to `SessionRegistry` and are tested there. What lives here
 * is the ORDER of SIP actions, which is where live calls get dropped — hold
 * before dialling, terminate before unhold, REFER on the primary rather than
 * the consultation. Keeping it out of the hook makes that order testable
 * without a SIP stack.
 */
import type { SessionRegistry, SipSession } from './session-registry.js'

export interface AttendedTransferDeps {
  registry: SessionRegistry
  hold: (session: SipSession) => void
  unhold: (session: SipSession) => void
  terminate: (session: SipSession) => void
  /**
   * REFER `session` (the caller) to the party on `replaces` (the consultation
   * leg). The consultation session — not a string — is the target, so the hook
   * can read its remote URI and attach RFC 3891 Replaces; the callee then
   * joins the consultation's existing dialog instead of ringing the target
   * from scratch.
   */
  refer: (session: SipSession, replaces: SipSession) => void
  /** Places the consultation call and returns its session. */
  placeCall: (target: string) => SipSession
}

export interface AttendedTransferResult {
  ok: boolean
  reason?: string
}

export interface AttendedTransferController {
  /** Hold the caller, then dial the transfer target as a SECOND leg. */
  startConsultation: (target: string) => AttendedTransferResult
  /** End the consultation and return the agent to the caller. */
  cancelConsultation: () => AttendedTransferResult
  /** REFER the caller to the target and drop out. */
  completeTransfer: () => AttendedTransferResult
  /** True when both legs are up, i.e. a transfer may be completed. */
  canComplete: () => boolean
}

export function createAttendedTransfer(
  deps: AttendedTransferDeps,
): AttendedTransferController {
  return {
    startConsultation(target: string): AttendedTransferResult {
      const primary = deps.registry.get('primary')
      if (!primary) {
        return { ok: false, reason: 'no active call to transfer' }
      }
      if (deps.registry.get('consult')) {
        // The registry would THROW on a double-set (deliberately — silent
        // replacement is the orphaning defect). Refuse cleanly instead, so a
        // double-click on the UI is not an exception in an event handler.
        return { ok: false, reason: 'a consultation is already in progress' }
      }

      // HOLD BEFORE DIALLING. Reversed, the caller hears the agent's entire
      // conversation with the transfer target.
      try {
        deps.hold(primary)
      } catch {
        // Hold can fail on a session mid-teardown — still try the consult
        // leg; the agent can cancel if it goes wrong.
      }

      let consult: SipSession
      try {
        consult = deps.placeCall(target)
      } catch {
        // The leg never went out — give the caller back instead of leaving
        // them muted on hold with the agent thinking the consult started.
        try {
          deps.unhold(primary)
        } catch { /* ignore */ }
        return { ok: false, reason: 'could not place the consultation call' }
      }
      // JsSIP fires `newRTCSession` synchronously from inside `ua.call()`, and
      // the hook's handler already files a second session under `consult`.
      // Setting it again would throw ("Refusing to replace the live consult
      // session") — only claim the slot if the handler did not.
      const filed = deps.registry.get('consult')
      if (filed === null) {
        deps.registry.set('consult', consult)
      } else if (filed !== consult) {
        return { ok: false, reason: 'a different consultation is already in progress' }
      }
      return { ok: true }
    },

    cancelConsultation(): AttendedTransferResult {
      const plan = deps.registry.planCancelConsultation()
      if (!plan.endSession) {
        return { ok: false, reason: 'no consultation in progress' }
      }

      deps.terminate(plan.endSession)
      deps.registry.clear('consult')

      // `resumeSession` is null when the caller hung up mid-consult. Acting on
      // it would throw inside a click handler; the registry models that case
      // precisely so this branch can be explicit rather than optional-chained.
      if (plan.resumeSession) {
        deps.unhold(plan.resumeSession)
      }
      return { ok: true }
    },

    completeTransfer(): AttendedTransferResult {
      if (!deps.registry.canCompleteTransfer()) {
        // Completing with only a primary IS a blind transfer — a different verb
        // with different consent implications for the caller. Refuse rather
        // than quietly performing one under the attended label.
        return { ok: false, reason: 'no consultation to transfer to' }
      }

      const primary = deps.registry.get('primary')
      const consult = deps.registry.get('consult')
      if (!primary || !consult) {
        return { ok: false, reason: 'a leg went away before completion' }
      }

      // The REFER goes on the PRIMARY leg: it is the caller who is being sent
      // to the target. Referring the consultation would transfer the wrong party.
      // The target is the consultation SESSION (was the literal string
      // 'consult', which REFERred the caller to sip:consult@… — a dead number).
      deps.refer(primary, consult)

      deps.registry.clear('consult')
      deps.registry.clear('primary')
      return { ok: true }
    },

    canComplete(): boolean {
      return deps.registry.canCompleteTransfer()
    },
  }
}
